import { useState, useCallback } from 'react';
import { addAnchor, updateAnchor, deleteAnchor } from '../../services/propertyRequestService';
import CommuteAnchorForm from './CommuteAnchorForm';
import CommuteIsochroneMap from './CommuteIsochroneMap';

const MODE_LABELS = { walk: 'پیاده', bike: 'دوچرخه', drive: 'خودرو', transit: 'حمل‌ونقل عمومی' };

/**
 * Props:
 *   requestId, anchors, setAnchors
 *   onPolygonsChange(rings) — called when isochrone polygons update (for overlay map)
 */
export default function CommuteTab({ requestId, anchors, setAnchors, onPolygonsChange }) {
    const [showForm,   setShowForm]   = useState(false);
    const [editAnchor, setEditAnchor] = useState(null);
    const [saving,     setSaving]     = useState(false);
    const [error,      setError]      = useState('');
    const showMap = anchors.length > 0 && !showForm && !editAnchor;

    const handleAdd = async (data) => {
        setError(''); setSaving(true);
        try { const a = await addAnchor(requestId, data); setAnchors(prev => [...prev, a]); setShowForm(false); }
        catch { setError('خطا در افزودن مقصد.'); } finally { setSaving(false); }
    };

    const handleEdit = async (data) => {
        setError(''); setSaving(true);
        try {
            const updated = await updateAnchor(requestId, editAnchor.id, data);
            setAnchors(prev => prev.map(a => a.id === editAnchor.id ? updated : a));
            setEditAnchor(null);
        } catch { setError('خطا در ویرایش مقصد.'); } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        setError('');
        try { await deleteAnchor(requestId, id); setAnchors(prev => prev.filter(a => a.id !== id)); }
        catch { setError('خطا در حذف مقصد.'); }
    };

    const handlePolygonsChange = useCallback((polys) => {
        if (onPolygonsChange) onPolygonsChange(polys);
    }, [onPolygonsChange]); // eslint-disable-line

    return (
        <div>
            {anchors.length > 0 && (
                <ul className="s6-anchor-list">
                    {anchors.map(a => (
                        <li key={a.id} className="s6-anchor-item">
                            <div className="s6-anchor-info">
                                <strong>{a.label}</strong>
                                <span className="s6-anchor-meta">{MODE_LABELS[a.mode] || a.mode} · {a.max_duration_min} دقیقه · {a.days_per_week} روز/هفته</span>
                            </div>
                            <div className="s6-anchor-actions">
                                <button type="button" className="prw-btn prw-btn-secondary s6-btn-sm"
                                    onClick={() => { setEditAnchor(a); setShowForm(false); }}>ویرایش</button>
                                <button type="button" className="s6-del-btn" onClick={() => handleDelete(a.id)} aria-label="حذف">✕</button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editAnchor && (
                <div className="s6-form-wrap">
                    <p className="s6-form-title">ویرایش مقصد</p>
                    <CommuteAnchorForm initial={editAnchor} onSave={handleEdit} onCancel={() => setEditAnchor(null)} />
                </div>
            )}

            {showForm && !editAnchor && (
                <div className="s6-form-wrap">
                    <p className="s6-form-title">افزودن مقصد جدید</p>
                    <CommuteAnchorForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
                </div>
            )}

            {!showForm && !editAnchor && (
                <button type="button" className="prw-btn prw-btn-secondary s6-add-btn" onClick={() => setShowForm(true)} disabled={saving}>
                    + افزودن مقصد
                </button>
            )}

            {error && <p className="prw-error">{error}</p>}

            {showMap && (
                <div className="s6-map-section">
                    <p className="s6-map-title">نقشه محدوده دسترسی{anchors.length > 1 && ' — ناحیه تلاقی، بهترین موقعیت سکونت است'}</p>
                    <CommuteIsochroneMap
                        anchors={anchors}
                        requestId={requestId}
                        onNeighborhoodsChange={() => {}}
                        onPolygonsChange={handlePolygonsChange}
                    />
                </div>
            )}
        </div>
    );
}
